import { useState, useEffect, useMemo } from 'react';
import { BackLink } from '@molecules/BackLink/BackLink';
import { ReportCard } from '@molecules/ReportCard/ReportCard';
import { SectionHeader } from '@atoms/SectionHeader/SectionHeader';
import { Pagination } from '@molecules/Pagination/Pagination';
import { StatusFilter } from '../../molecules/StatusFilter/StatusFilter';
import { CategoryFilter } from '../../molecules/CategoryFilter/CategoryFilter';
import { SortFilter } from '../../molecules/SortFilter/SortFilter';
import {
  LOADING_REPORTS_MESSAGE,
  REPORT_STATUS_COLORS,
  REPORTS_WITH_STATUS_UPDATES,
  type ReportStatus,
} from '../../../constants';
import {
  useMediaQuery,
  useUserServices,
  useUserStatuses,
  useUserCases,
} from '../../../hooks';
import { RoutesList } from '../../../routes/Routes';

const MOBILE_ITEMS_PER_PAGE = 6;
const DESKTOP_ITEMS_PER_PAGE = 10;

const sortOptions = [
  { label: 'Newest first', value: 'created_at_desc' },
  { label: 'Oldest first', value: 'created_at_asc' },
  { label: 'Recently updated', value: 'updated_at_desc' },
  { label: 'Least recently updated', value: 'updated_at_asc' },
];

export const ReportsList = () => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatuses, setSelectedStatuses] = useState<ReportStatus[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSort, setSelectedSort] = useState('created_at_desc');
  const [availableStatuses, setAvailableStatuses] = useState<ReportStatus[]>(
    [],
  );

  const itemsPerPage = isMobile
    ? MOBILE_ITEMS_PER_PAGE
    : DESKTOP_ITEMS_PER_PAGE;

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const { data: servicesData, isPending: isLoadingServices } =
    useUserServices();

  const { data: statusesData } = useUserStatuses();

  const availableCategories = useMemo(
    () => (servicesData || []).map((s) => s.name).sort(),
    [servicesData],
  );

  // Initialize categories when services load
  useEffect(() => {
    if (availableCategories.length > 0) {
      setSelectedCategories((prev) =>
        prev.length === 0 ? availableCategories : prev,
      );
    }
  }, [availableCategories]);

  const {
    data: casesData,
    isFetching,
    error: queryError,
  } = useUserCases({
    page: currentPage,
    pageSize: itemsPerPage,
    sort: selectedSort,
    selectedStatuses,
    availableStatuses,
    selectedCategories,
    availableCategories,
    userServices: servicesData || [],
  });

  const reports = casesData?.cases ?? [];
  const totalReports = casesData?.total ?? 0;
  const totalPages =
    totalReports > 0 ? Math.ceil(totalReports / itemsPerPage) : 1;

  // Accumulate available statuses from fetched data
  useEffect(() => {
    if (statusesData) {
      setAvailableStatuses(
        () =>
          statusesData.map((status) => status.name).sort() as ReportStatus[],
      );
    }
  }, [statusesData]);

  const shouldShowStatusFilter =
    totalReports >= 2 || availableStatuses.length >= 2;
  const showStatusFilter = shouldShowStatusFilter;

  const shouldShowCategoryFilter =
    totalReports >= 2 || availableCategories.length >= 2;
  const showCategoryFilter = shouldShowCategoryFilter && !isLoadingServices;

  const handleStatusChange = (selected: ReportStatus[]) => {
    setSelectedStatuses(selected);
    setCurrentPage(1);
  };

  const handleCategoryChange = (selected: string[]) => {
    setSelectedCategories(selected);
    setCurrentPage(1);
  };

  const handleSortChange = (value: string) => {
    setSelectedSort(value);
    setCurrentPage(1);
  };

  const errorMessage =
    queryError instanceof Error
      ? queryError.message
      : 'Failed to load reports. Please try again later.';

  return (
    <div className="section-horizontal-padding mb-12">
      <BackLink to={RoutesList.HOME} label="Back to MyWestminster dashboard" />
      <SectionHeader
        title="All my reports"
        padding="py-6 px-4"
        headingClassName=""
      />
      <hr className="border-ui-border mb-6 mt-6" />

      <div className="mb-6 flex flex-wrap gap-4">
        {showStatusFilter && (
          <StatusFilter
            statuses={availableStatuses}
            selectedStatuses={selectedStatuses}
            onSelectionChange={handleStatusChange}
          />
        )}

        {showCategoryFilter && (
          <CategoryFilter
            categories={availableCategories}
            selectedCategories={selectedCategories}
            onSelectionChange={handleCategoryChange}
          />
        )}

        <SortFilter
          options={sortOptions}
          value={selectedSort}
          onChange={handleSortChange}
        />
      </div>

      <div className="mb-6 flex flex-col gap-6">
        {isFetching ? (
          <p className="text-body text-text-body">{LOADING_REPORTS_MESSAGE}</p>
        ) : queryError ? (
          <p className="text-body text-text-body">{errorMessage}</p>
        ) : reports.length > 0 ? (
          reports.map((report) => (
            <ReportCard
              key={report.id}
              id={report.id}
              title={report.title}
              caseReference={report.caseReference}
              dateCreated={report.dateCreated}
              dateUpdated={report.dateUpdated}
              status={report.status}
              colors={REPORT_STATUS_COLORS}
              hideLink={!REPORTS_WITH_STATUS_UPDATES.includes(report.title)}
            />
          ))
        ) : (
          <p className="text-body text-text-body">
            No reports match the selected filters.
          </p>
        )}
      </div>

      {!isFetching && !queryError && totalReports > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          maxVisible={isMobile ? 5 : 7}
        />
      )}
    </div>
  );
};
